import { useJsApiLoader } from '@react-google-maps/api'
import _ from 'lodash'
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useState,
} from 'react'
import Supercluster, { type PointFeature } from 'supercluster'

import {
  defaultCenter,
  normalizeLatLng,
} from '@/entities/hotel/utils/map/normalizeCoords'
import { env } from '@/env'
import { sc, scOptions } from '@/lib/supercluster'
import { CacheAdapter } from '@/shared/infra'

const libraries: ('places' | 'drawing' | 'geometry' | 'visualization')[] = [
  'geometry',
  'places',
]

export interface Circle {
  center: google.maps.LatLngLiteral
  radius: number
}

interface MapContextProps {
  isMapLoaded: boolean
  map: google.maps.Map | null
  setMap: (map: google.maps.Map | null) => void
  clusters: any[]
  setClusters: (clusters: any[]) => void
  updateClusters: () => void
  loadSuperCluster: (points: PointFeature<any>[]) => void
  center: google.maps.LatLngLiteral
  setCenter: (coords: google.maps.LatLngLiteral) => void
  circle: Circle | null
  setCircle: (
    circle: Circle | ((prevCircle: Circle | null) => Circle | null) | null,
  ) => void
  markerMode: boolean
  setMarkerMode: (mode: boolean) => void
  superCluster: typeof sc | null
  enableRadius: boolean
  setEnableRadius: (enable: boolean) => void
  showMap: boolean
  setShowMap: (value: boolean) => void
  showBackground: boolean
  setShowBackground: (value: boolean) => void
  reloadMap?: () => void
  handleRadiusChange: (
    radius: number,
    center?: google.maps.LatLngLiteral,
  ) => void
  saveMapState: () => void
  restoreMapState: () => void
  coordsLoaded: boolean
  setCoordsLoaded: (value: boolean) => void
  isMapLoading: boolean
  setIsMapLoading: (value: boolean) => void
  circleChanged: boolean
  setCircleChanged: (value: boolean) => void
  initialCircle: Circle | null
  setInitialCircle: (circle: Circle | null) => void
  MAX_RADIUS_KM: number
  canShowActions: boolean
  setCanShowActions: (value: boolean) => void
}

const MapContext = createContext<MapContextProps | undefined>(undefined)

export const MapProvider = ({ children }: { children: ReactNode }) => {
  const { isLoaded: isMapLoaded } = useJsApiLoader({
    googleMapsApiKey: (env.VITE_GOOGLE_MAP_KEY as string) || '',
    libraries,
  })

  const [map, setMap] = useState<google.maps.Map | null>(null)
  const [clusters, setClusters] = useState<any[]>([])
  const [center, setCenterState] =
    useState<google.maps.LatLngLiteral>(defaultCenter)
  const [circle, setCircleState] = useState<Circle | null>(null)
  const [markerMode, setMarkerMode] = useState<boolean>(false)
  const [enableRadius, setEnableRadius] = useState<boolean>(false)
  const [showMap, setShowMap] = useState<boolean>(false)
  const [showBackground, setShowBackground] = useState<boolean>(true)
  const [coordsLoaded, setCoordsLoaded] = useState<boolean>(false)
  const [isMapLoading, setIsMapLoading] = useState<boolean>(true)
  const [circleChanged, setCircleChanged] = useState<boolean>(false)
  const [initialCircle, setInitialCircle] = useState<Circle | null>(null)
  const [canShowActions, setCanShowActions] = useState(false)

  const MAX_RADIUS_KM = 10

  const loadSuperCluster = (points: PointFeature<any>[]): void => {
    if (!sc) {
      console.warn(
        'Supercluster instance was undefined, initializing a new instance.',
      )
      const forceScInitialize = new Supercluster(scOptions)

      forceScInitialize.load(points)
      updateClusters()
    } else {
      sc.load(points)
      updateClusters()
    }
  }

  const getClustersForViewport = (
    bounds: google.maps.LatLngBounds | undefined,
    zoom: number | undefined,
  ): any[] => {
    if (!bounds || !zoom) return []

    const clusters = sc.getClusters(
      [
        bounds.getSouthWest().lng(),
        bounds.getSouthWest().lat(),
        bounds.getNorthEast().lng(),
        bounds.getNorthEast().lat(),
      ],
      zoom,
    )
    return clusters
  }

  const debouncedUpdateClusters = _.debounce(() => {
    console.log('🔄 Clusters Update:', new Date().toISOString())
    if (sc && map && markerMode) {
      const bounds = map.getBounds()
      if (bounds) {
        console.log('🔄 Cluster Trigger:', {
          markerMode,
          hasSuperCluster: !!sc,
          hasBounds: !!bounds,
          zoom: map.getZoom(),
        })

        const zoom = map.getZoom() as number
        const newClusters = getClustersForViewport(bounds, zoom)

        if (!_.isEqual(clusters, newClusters)) {
          console.log('🔄 Clusters Changed:', {
            oldCount: clusters.length,
            newCount: newClusters.length,
          })
          setClusters(newClusters)
        } else {
          console.log('🔄 Clusters Unchanged - Skip Update')
        }
      }
    } else {
      console.log('🔄 Cluster Update Skipped:', {
        hasSuperCluster: !!sc,
        hasMap: !!map,
        markerMode,
      })
    }
  }, 1000)

  const updateClusters = useCallback(() => {
    if (!markerMode) return
    debouncedUpdateClusters()
  }, [debouncedUpdateClusters, markerMode])
  const handleRadiusChange = (
    radius: number,
    circleCenter?: google.maps.LatLngLiteral,
  ) => {
    if (circleCenter) {
      setCircleState(prevCircle => {
        const normalizedCenter = normalizeLatLng(
          circleCenter,
          prevCircle?.center ?? center,
        )
        const newCircle = { center: normalizedCenter, radius }
        if (
          prevCircle &&
          (prevCircle.radius !== radius ||
            prevCircle?.center?.lat !== normalizedCenter.lat ||
            prevCircle?.center.lng !== normalizedCenter.lng)
        ) {
          setCircleChanged(true)
        }
        return newCircle
      })
    } else if (circle) {
      setCircleState(prevCircle => {
        if (prevCircle) {
          const newCircle = { ...prevCircle, radius }
          if (prevCircle.radius !== radius) {
            setCircleChanged(true)
          }
          return newCircle
        }
        return prevCircle
      })
    }
  }

  const reloadMap = () => {
    if (map) {
      const currentCenter = map.getCenter()
      if (currentCenter) {
        map.panTo(currentCenter)
      }
    }
  }

  const saveMapState = () => {
    if (map) {
      const currentCenter = map.getCenter()
      const zoom = map.getZoom()
      const mapState = {
        center: currentCenter
          ? normalizeLatLng(
              { lat: currentCenter.lat(), lng: currentCenter.lng() },
              center,
            )
          : null,
        zoom,
        showMap,
        coordsLoaded,
        isMapLoading,
      }

      CacheAdapter.set('mapState', mapState)
    }
  }

  const restoreMapState = () => {
    const savedState = CacheAdapter.get('mapState')

    if (Object.keys(savedState).length > 0) {
      const { center, zoom, showMap, coordsLoaded, isMapLoading } = savedState

      if (center && center.lat !== 0 && center.lng !== 0) {
        setCenterState(prevCenter => normalizeLatLng(center, prevCenter))

        if (zoom && map) map.setZoom(zoom)
        setShowMap(showMap)
        setCoordsLoaded(coordsLoaded)
        setIsMapLoading(isMapLoading)
        map?.panTo(normalizeLatLng(center))
      } else {
        console.error('Invalid center coordinates', center)
        CacheAdapter.set('mapState')
      }
    }
  }

  const normalizeCircle = (
    nextCircle: Circle | null,
    prevCircle: Circle | null,
  ): Circle | null => {
    if (!nextCircle) {
      return null
    }

    const sanitizedCenter = normalizeLatLng(
      nextCircle.center ?? prevCircle?.center ?? center,
      prevCircle?.center ?? center,
    )

    return {
      ...nextCircle,
      center: sanitizedCenter,
    }
  }

  const safeSetCircle: MapContextProps['setCircle'] = newCircle => {
    if (typeof newCircle === 'function') {
      setCircleState(prevCircle => {
        const resolved = newCircle(prevCircle)
        return normalizeCircle(resolved, prevCircle)
      })
      return
    }

    setCircleState(prevCircle => normalizeCircle(newCircle, prevCircle))
  }

  const safeSetCenter: MapContextProps['setCenter'] = coords => {
    setCenterState(prev => normalizeLatLng(coords, prev))
  }

  return (
    <MapContext.Provider
      value={{
        isMapLoaded,
        map,
        setMap,
        clusters,
        setClusters,
        updateClusters,
        loadSuperCluster,
        superCluster: sc,
        center,
        setCenter: safeSetCenter,
        circle,
        setCircle: safeSetCircle,
        markerMode,
        setMarkerMode,
        enableRadius,
        setEnableRadius,
        showMap,
        setShowMap,
        showBackground,
        setShowBackground,
        reloadMap,
        handleRadiusChange,
        saveMapState,
        restoreMapState,
        coordsLoaded,
        setCoordsLoaded,
        isMapLoading,
        setIsMapLoading,
        circleChanged,
        setCircleChanged,
        initialCircle,
        setInitialCircle,
        MAX_RADIUS_KM,
        canShowActions,
        setCanShowActions,
      }}
    >
      {children}
    </MapContext.Provider>
  )
}

export const useMapContext = (): MapContextProps => {
  const context = useContext(MapContext)
  if (!context) {
    throw new Error('useMapContext must be used within a MapProvider')
  }
  return context
}
